/** @jsxImportSource @emotion/react */
import { css, Theme as EmotionTheme } from '@emotion/react';

import { useState, useEffect, useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Frozen from "@amcharts/amcharts5/themes/Kelly";
import { addWeeks, sub, subWeeks } from 'date-fns';
import { Box, CircularProgress, useTheme } from "@mui/material/";
import { randGenSeed, randWithSeed } from '../../../common/CustomFunctions';
import { useTranslation } from 'react-i18next';
import OpenEntriesService from '../../../service/OpenEntriesService';
import { useParams } from 'react-router-dom';
import ErrorMessageContainer from '../../../common/components/ErrorMessageContainer';
import EmptyGraphMessageContainer from '../../../common/components/EmptyGraphMessageContainer';
import { digiAccActorType } from '../../../common/TextTypes';


//This chart is not like the oter ones. This treat each product or actor as its own graph as a series.
//Example you define toActor or fromActor and the actor prop contains multiple actors. product prop is empty.

const chartFrame = css({
    height: "100%",
    width: "100%"
});

export interface IProps {
    //clientId: string,
    //fromDate?: Date,
    //toDate?: Date,
    type: digiAccActorType,
    includeEstimate?: boolean;
    includeTimeSpan?: boolean;
    chartName?: string;
    chartTitle?: string;
    disableXAxis?: boolean;
    disableYAxis?: boolean;
    disableYGrid?: boolean;
    disableXGrid?: boolean;
    removePadding?: boolean;
    preZoomed?: boolean;

}
interface childData {
    name: string,
    value: number,
}
interface rootData {
    name: string,
    children: childData[],
};


const PartitionChartOutstandingInvoices = (props: IProps): JSX.Element => {

    const theme = useTheme();

    const { organizationId } = useParams();

    const { t, i18n } = useTranslation();

    const root = useRef<am5.Root | null>(null);
    const chart = useRef(null);
    //##
    const series = useRef<am5hierarchy.Partition>();
    const [data, setData] = useState<rootData[]>([]);
    const [dataError, setDataError] = useState(false);
    const [loader, setLoader] = useState(true);

    let name = "PartitionChart";

    if (props.chartName !== undefined && props.chartName !== null) {
        name = props.chartName;
    }

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        setLoader(true);

        const getCustomer = async () => {
            const service = new OpenEntriesService();
            const result = await service.getCustomers(organizationId);
            if (result.hasErrors) {
                setDataError(true);
            } else {
                setDataError(false);
                if (isMounted) {
                    let children = [];
                    children.push(...result.value.map(e => {
                        return {name: e.actorName, value: e.outstanding}
                    }))
                    setData([{name: "Sum", children: children.sort((a, b) => b.value - a.value)}]);
                }
            }
        }
        const getSupplier = async () => {
            const service = new OpenEntriesService();
            const result = await service.getSuppliers(organizationId);
            if (result.hasErrors) {
                setDataError(true);
            } else {
                setDataError(false);
                if (isMounted) {
                    let children = [];
                    children.push(...result.value.map(e => {
                        return { name: e.actorName, value: e.outstanding }
                    }))
                    setData([{ name: "Sum", children: children.sort((a, b) => b.value - a.value) }]);
                }
            }
        }

        if (props.type === "Customer")
            getCustomer();
        else if (props.type === "Supplier")
            getSupplier();

        setLoader(false);

        return () => { isMounted = false };

    }, [organizationId]);

    useLayoutEffect(() => { //Gets triggerred before useEffect.

        //#################################################################################
        //#################################Root############################################
        let root_ = am5.Root.new(name);
        root_.setThemes([
            am5themes_Animated.new(root_),
            props.type === "Customer" ? am5themes_Frozen.new(root_) : am5themes_Animated.new(root_),
        ]);

        root_.numberFormatter.set("numberFormat", "#,###.00");

        root.current = root_;
        //#################################################################################
        //#################################Chart###########################################
        let container = root_.container.children.push(am5.Container.new(root_, {
            width: am5.percent(100),
            height: am5.percent(100),
            layout: root_.verticalLayout
        }));

        let series_ = container.children.push(am5hierarchy.Partition.new(root_, {
            singleBranchOnly: false,
            orientation: "horizontal",
            downDepth: 1,
            initialDepth: 10, //How many to show initially
            topDepth: 0, //Ignore root node, 0 is root node.
            valueField: "value",
            categoryField: "name",
            childDataField: "children",
            nodePadding: 3,
        }));

        series_.labels.template.setAll({
            text: "{category}:\n[bold]{sum}[/] kr",
            fill: am5.color(theme.palette.text.primary),
            fontSize: 14
        });
        //series_.rectangles.template.set("tooltipText", "{category}: [bold]{sum}[/]");

        series.current = series_;

        return () => {
            root_.dispose();
        }

    }, []);

    useEffect(() => {
        if (series.current && data && data.length > 0) {
            //Tells which node we start with as top node.
            series.current.data.setAll(data);
            series.current.set("selectedDataItem", series.current.dataItems[0]);
        }
    }, [data]);

    const ChartRenderMessage = (): JSX.Element => {
        if (loader) {
            return <CircularProgress color="secondary" sx={{position:"absolute"}} />
        } else if (dataError) {
            return <Box position="absolute"><ErrorMessageContainer /></Box>
        } else if (data.length < 1) {
            return <Box position="absolute"><EmptyGraphMessageContainer /></Box>
        } else {
            return <></>
        }
    }

    return <div id={name} css={chartFrame}>
        <ChartRenderMessage/>
    </div>
}
export default PartitionChartOutstandingInvoices